import { client } from "../../infrastructure/graphql/client";

import {
  LOGIN_MUTATION,
  VERIFY_OTP_MUTATION,
  FORGOT_PASSWORD_MUTATION,
  VERIFY_RESET_OTP_MUTATION,
  RESET_PASSWORD,
  ME_QUERY,
  LOGOUT_MUTATION,
  CREATE_USER_MUTATION,
  GET_ALL_USERS_QUERY,
  GET_ALL_USERS_EXCEPT_ME_QUERY,
  CHANGE_USER_ROLE_MUTATION,
} from "../../infrastructure/graphql/authQuerries";
import {
  clearAuthData,
  getToken,
} from "../../infrastructure/storage/tokenStorage";

export class AuthService {
  async login(email, password) {
    try {
      const result = await client.mutate({
        mutation: LOGIN_MUTATION,
        variables: { email, password },
      });

      const response = result.data.login;

      if (response.message && response.message.includes("otp sent")) {
        return {
          success: true,
          requiresOtp: true,
          email: email,
          message: response.message,
        };
      }

      return {
        success: false,
        message: response.message || "login failed",
      };
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.graphQLErrors?.[0]?.message || "Network error";
      return { success: false, message: errorMessage };
    }
  }

  async verifyLoginOtp(email, otp) {
    try {
      const result = await client.mutate({
        mutation: VERIFY_OTP_MUTATION,
        variables: { email, otp },
      });

      const response = result.data.verifyOtp;

      if (response.token) {
        return {
          success: true,
          email: email,
          token: response.token,
          role: response.role,
        };
      }

      return {
        success: false,
        message: response.message || "otp verification failed",
      };
    } catch (error) {
      console.error(error);
      const errorMessage = error.graphQLErrors?.[0]?.message || "Invalid OTP";
      return { success: false, message: errorMessage };
    }
  }

  async forgetPassword(email) {
    try {
      const result = await client.mutate({
        mutation: FORGOT_PASSWORD_MUTATION,
        variables: { email },
      });

      const response = result.data.forgetPassword;

      if (response.message && response.message.includes("reset code sent")) {
        return {
          success: true,
          email: email,
          message: response.message,
        };
      }
      return {
        success: false,
        message: response.message || "failed to send code",
      };
    } catch (error) {
      console.error("Forgot password error:", error);
      const errorMessage =
        error.graphQLErrors?.[0]?.message || "Email not found";
      return { success: false, message: errorMessage };
    }
  }

  async verifyResetOtp(email, otp) {
    try {
      const result = await client.mutate({
        mutation: VERIFY_RESET_OTP_MUTATION,
        variables: { email, otp },
      });

      const response = result.data.verifyResetOtp;

      // Backend returns: { message: "OTP verified successfully. You can now reset your password." }
      if (
        response.message &&
        response.message.includes("OTP verified successfully")
      ) {
        return {
          success: true,
          message: response.message,
        };
      }

      return {
        success: false,
        message: response.message || "OTP verification failed",
      };
    } catch (error) {
      console.error("Reset OTP verification error:", error);
      const errorMessage = error.graphQLErrors?.[0]?.message || "Invalid OTP";
      return { success: false, message: errorMessage };
    }
  }

  async resetPassword(email, otp, newPassword) {
    try {
      const result = await client.mutate({
        mutation: RESET_PASSWORD,
        variables: { email, otp, newPassword },
      });

      const response = result.data.resetPassword;

      if (
        response.message &&
        response.message.includes("Password reset successfully")
      ) {
        return {
          success: true,
          email: email,
          message: response.message,
        };
      } else {
        return {
          success: false,
          message: response.message,
        };
      }
    } catch (error) {
      console.error("Reset OTP verification error:", error);
      const errorMessage = error.graphQLErrors?.[0]?.message || "Invalid OTP";
      return { success: false, message: errorMessage };
    }
  }

  async getCurrentUser() {
    try {
      const result = await client.query({
        query: ME_QUERY,
        fetchPolicy: "network-only",
      });

      const user = result.data.me;
      return {
        success: true,
        user: { email: user.email, role: user.role },
      };
    } catch (error) {
      console.error("Token validation failed:", error);

      this.logout();
      return {
        success: false,
        error: error.graphQLErrors?.[0]?.message || "Session expired",
      };
    }
  }

  async logout() {
    try {
      const response = await client.mutate({
        mutation: LOGOUT_MUTATION,
      });

      if (response.data?.logout) {
        clearAuthData();

        return {
          success: true,
          message: response.data.logout.message || "Logged out successfully",
        };
      }

      return {
        success: false,
        message: "Logout failed",
      };
    } catch (error) {
      console.error("Logout error:", error);
      clearAuthData();

      return {
        success: true,
        message: "Logged out locally",
      };
    }
  }

  async createUser(email, role) {
    try {
      const result = await client.mutate({
        mutation: CREATE_USER_MUTATION,
        variables: { email, role },
      });

      const response = result.data.createUser;

      if (response.email) {
        return {
          success: true,
          email: response.email,
          message: response.message,
        };
      }

      return {
        success: false,
        message: response.message || "Failed to create user",
      };
    } catch (error) {
      console.error("Create user error:", error);
      const errorMessage = error.graphQLErrors?.[0]?.message || "Network error";
      return { success: false, message: errorMessage };
    }
  }

  async getAllUsers() {
    try {
      const result = await client.query({
        query: GET_ALL_USERS_QUERY,
        fetchPolicy: "network-only",
      });

      const users = result.data.getAllUsers;
      return {
        success: true,
        users: users,
      };
    } catch (error) {
      console.error("Get all users error:", error);
      const errorMessage =
        error.graphQLErrors?.[0]?.message || "Failed to fetch users";
      return { success: false, message: errorMessage };
    }
  }

  async getAllUsersExceptMe() {
    try {
      const result = await client.query({
        query: GET_ALL_USERS_EXCEPT_ME_QUERY,
        fetchPolicy: "network-only",
      });

      const users = result.data.getAllUsersExceptMe;
      return {
        success: true,
        users: users,
      };
    } catch (error) {
      console.error("Get users except me error:", error);
      const errorMessage =
        error.graphQLErrors?.[0]?.message || "Failed to fetch users";
      return { success: false, message: errorMessage };
    }
  }

  async changeUserRole(targetUserEmail, newRole) {
    try {
      const result = await client.mutate({
        mutation: CHANGE_USER_ROLE_MUTATION,
        variables: { targetUserEmail, newRole },
      });

      const response = result.data.changeUserRole;

      return {
        success: true,
        message: response,
      };
    } catch (error) {
      console.error("Change user role error:", error);
      const errorMessage =
        error.graphQLErrors?.[0]?.message || "Failed to change role";
      return { success: false, message: errorMessage };
    }
  }

  isAuthenticated() {
    return !!getToken();
  }
}
